import _ from 'lodash/fp'
import uuid from 'uuid'
import { createActions, createTypes, createReducer, createSelector, assignLeft } from '../util'

export const namespace = 'matches.games'

const typeKeys = [
  'ADD',
  'FINISH_REQUEST', // saga thing
  'FINISH',
  'FINISH_MORE',
  'DISCARD_REQUEST', // saga thing
  'DISCARD',
  'ADD_SHOT',
  'SUBTRACT_SHOT',
  'ADJUST_SHOTS',
  'ADJUST_HANDICAP',
  'ADJUST_PLAYED_AT',
  'MERGE',
  'CLEAR',
  'SEND_GAME', // saga thing
  'SEND_GAME_ERROR', // saga thing
  'SEND_GAME_SUCCESS' // saga thing
]

export const types = createTypes(typeKeys, namespace)

export const actions = createActions(types)

export const initialState = {
  items: {}
}

const invalidAdd = _.flow(
  _.at(['userId', 'handicap']),
  _.values,
  _.some(_.isNil)
)

const reducer = {
  [types.ADD]: payload => state => {
    if (invalidAdd(payload)) {
      console.error('Invalid payload in \'games/ADD\'')
      return state
    }
    const metaData = { gameId: uuid.v4(), createdAt: Date.now(), d: 1 }
    // if a gameId is on the game, then it is not overwritten by metaData
    const decoratedGame = assignLeft(payload, metaData)
    const gameId = _.get('gameId', decoratedGame)
    return _.set(`items.${gameId}`, decoratedGame, state)
  },

  [types.FINISH]: payload => state => {
    const gameId = _.get('gameId', payload)
    if (!gameId) {
      return state
    }
    return _.update(`items.${gameId}`, assignLeft({ finishedAt: Date.now(), d: 1 }), state)
  },

  [types.FINISH_MORE]: payload => state => {
    const gameIds = _.get('gameIds', payload)
    if (!gameIds) {
      return state
    }
    return _.reduce((acc, gameId) => {
      return _.update(`items.${gameId}`, assignLeft({ finishedAt: Date.now(), d: 1 }), acc)
    }, state, gameIds)
  },

  [types.DISCARD]: payload => state => {
    const gameId = _.get('gameId', payload)
    if (!gameId) {
      return state
    }
    return _.update(`items.${gameId}`, assignLeft({ discardedAt: Date.now(), d: 1 }), state)
  },

  [types.ADJUST_HANDICAP]: payload => state => {
    const gameId = _.get('gameId', payload)
    const handicap = _.get('handicap', payload)
    if (!gameId || !handicap) {
      return state
    }
    return _.update(`items.${gameId}`, assignLeft({ handicap, d: 1 }), state)
  },

  [types.ADJUST_PLAYED_AT]: payload => state => {
    const gameId = _.get('gameId', payload)
    const playedAt = _.get('playedAt', payload)
    if (!gameId || !playedAt) {
      return state
    }
    return _.update(`items.${gameId}`, assignLeft({ playedAt, d: 1 }), state)
  },

  [types.MERGE]: payload => state => {
    const stateItems = _.get('items', state)
    const games = _.get('games', payload)
    if (!games || !stateItems) {
      return state
    }
    const syncedGames = _.reduce((localGames, game) => {
      return _.update(game.gameId, (localGame) => {
        const mergedHolesTees = _.isEmpty(_.get('holesTees', game))
          ? _.getOr({}, 'holesTees', localGame)
          : _.flow(_.get('holesTees'), _.keyBy('holeId'), _.mapValues(_.get('teeId')))(game)

        const finishedAtLocal = _.getOr(undefined, 'finishedAt', localGame)
        const finishedAtRemote = _.getOr(undefined, 'finishedAt', game)
        const finished = _.isEqual(Boolean(finishedAtLocal), Boolean(finishedAtRemote))
        const finishedAt = finishedAtRemote || finishedAtLocal

        const discardedAtLocal = _.getOr(undefined, 'discardedAt', localGame)
        const discardedAtRemote = _.getOr(undefined, 'discardedAt', game)
        const discarded = _.isEqual(Boolean(discardedAtLocal), Boolean(discardedAtRemote))
        const discardedAt = discardedAtRemote || discardedAtLocal

        const dirty = !finished || !discarded
        if (dirty) {
          return assignLeft({
            ...game,
            d: 1,
            finishedAt,
            discardedAt,
            holesTees: mergedHolesTees
          }, localGame)
        }

        return assignLeft({
          ...game,
          finishedAt,
          discardedAt,
          holesTees: mergedHolesTees
        }, _.omit('d', localGame))
      }, localGames)
    }, stateItems, games)
    return _.set('items', syncedGames, state)
  },

  [types.CLEAR]: p => _.constant(initialState)
}

export default createReducer(initialState, reducer)

// New fancy smart selectors
const _getGames = _.get(`${namespace}.items`)

// Supplying an extra filter function allows the selector to get the latest games of a special kind
// The function must be applicable on an array and should also return an array!
const getLatestGames = (n = 1, userId, customFilter = _.identity) => createSelector(
  _getGames,
  _.flow(
    _.sortBy((g) => (1 - _.get('finishedAt', g))),
    _.reject('discardedAt'),
    customFilter,
    _.filter((g) => _.get('userId', g) === userId),
    _.filter((g) => !_.isEmpty(_.get('holesShots', g))),
    _.take(n)
  )
)

export const selectors = {
  getGames: _getGames,
  getLatestGames
}
